import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

const LOCALE_MAP: Record<string, string> = {
  en: 'en-US',
  es: 'es-ES',
  'es-MX': 'es-MX',
  de: 'de-DE',
  'fr-FR': 'fr-FR',
  'fr-CA': 'fr-CA',
  it: 'it-IT',
  'pt-BR': 'pt-BR',
  'pt-PT': 'pt-PT',
} as const;

const ITALIAN_MONTH_ABBREVIATIONS: Record<string, string> = {
  gen: 'gen.',
  feb: 'feb.',
  mar: 'mar.',
  apr: 'apr.',
  mag: 'mag.',
  giu: 'giu.',
  lug: 'lug.',
  ago: 'ago.',
  set: 'set.',
  ott: 'ott.',
  nov: 'nov.',
  dic: 'dic.',
} as const;

const GERMAN_MONTH_ABBREVIATIONS: Record<string, string> = {
  Jan: 'Jan.',
  Feb: 'Feb.',
  Mär: 'März',
  Apr: 'Apr.',
  Mai: 'Mai',
  Jun: 'Jun.',
  Jul: 'Jul.',
  Aug: 'Aug.',
  Sep: 'Sept.',
  Okt: 'Okt.',
  Nov: 'Nov.',
  Dez: 'Dez.',
} as const;

const SPANISH_MONTH_ABBREVIATIONS: Record<string, string> = {
  ene: 'ene.',
  feb: 'feb.',
  mar: 'mar.',
  abr: 'abr.',
  may: 'may.',
  jun: 'jun.',
  jul: 'jul.',
  ago: 'ago.',
  sept: 'sept.',
  oct: 'oct.',
  nov: 'nov.',
  dic: 'dic.',
} as const;

@Injectable({
  providedIn: 'root',
})
export class LocaleService {
  constructor(private translocoService: TranslocoService) {}

  formatDate(date: Date | string | number): string {
    if (!date) return '';

    const dateObj = this.convertToUtcDate(date);
    if (isNaN(dateObj.getTime())) {
      return '';
    }

    const localeId = this.getCurrentLocaleId();
    const currentLang = this.getCurrentLanguage();

    const day = dateObj.getUTCDate();
    const month = this.formatMonthAbbreviation(dateObj, localeId, currentLang);
    const year = dateObj.getUTCFullYear();

    return this.formatDateString(day.toString(), month, year, currentLang);
  }

  private convertToUtcDate(date: Date | string | number): Date {
    const tempDate = new Date(date);
    if (isNaN(tempDate.getTime())) {
      return tempDate;
    }

    const useUtcMethods = typeof date === 'string' && date.includes('T') && date.includes('Z');

    const year = useUtcMethods ? tempDate.getUTCFullYear() : tempDate.getFullYear();
    const month = useUtcMethods ? tempDate.getUTCMonth() : tempDate.getMonth();
    const day = useUtcMethods ? tempDate.getUTCDate() : tempDate.getDate();

    return new Date(Date.UTC(year, month, day, 12, 0, 0));
  }

  formatDateTime(date: Date | string | number): string {
    const dateObj = this.toDateObject(date);
    if (!dateObj) return '';

    return this.formatDateTimeWithSeparator(dateObj, ' \u2022 ');
  }

  formatShortDateTimeWithTimezone(date: Date | string | number, timeZone: string = 'America/New_York'): string {
    const dateObj = this.toDateObject(date);
    if (!dateObj) return '';

    const localeId = this.getCurrentLocaleId();
    const currentLang = this.getCurrentLanguage();
    const use12Hour = this.shouldUse12HourFormat(currentLang);
    const tzOpt = timeZone;

    const opts: Intl.DateTimeFormatOptions = {
      timeZone: tzOpt,
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: use12Hour,
      timeZoneName: 'short',
    };

    const parts = new Intl.DateTimeFormat(localeId, opts).formatToParts(dateObj);
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';

    const month = this.applyLanguageSpecificMonthAbbreviation(get('month'), currentLang);
    const dateStr = this.formatDateString(get('day'), month, Number(get('year')) || dateObj.getFullYear(), currentLang);

    const timeFormatted = new Intl.DateTimeFormat(localeId, {
      timeZone: tzOpt,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: use12Hour,
    }).format(dateObj);
    const timeStr = this.applyCountrySpecificTimeFormatting(timeFormatted.replace(/:/g, '.'), currentLang);

    const tzStr =
      new Intl.DateTimeFormat('en-US', { timeZone: tzOpt, timeZoneName: 'short' })
        .formatToParts(dateObj)
        .find((p) => p.type === 'timeZoneName')?.value ?? '';

    return tzStr ? `${dateStr} \u2022 ${timeStr} ${tzStr}` : `${dateStr} \u2022 ${timeStr}`;
  }

  formatDateShort(date: Date | string | number): string {
    if (!date) return '';

    const localeId = this.getCurrentLocaleId();
    const dateObj = new Date(date);

    if (isNaN(dateObj.getTime())) {
      return '';
    }

    const currentLang = this.getCurrentLanguage();

    const month = this.formatMonthAbbreviation(dateObj, localeId, currentLang);
    const day = dateObj.getUTCDate();

    return `${month} ${day}`;
  }

  formatDateLong(date: Date | string | number): string {
    const dateObj = this.toDateObject(date);
    if (!dateObj) return '';

    return dateObj.toLocaleDateString(this.getCurrentLocaleId(), {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  formatDateTimeLong(date: Date | string | number): string {
    return this.formatDateTimeWithOptions(date, {
      day: 'numeric',
      hour: 'numeric',
    });
  }

  formatSessionDateTime(date: Date | string | number): string {
    const dateObj = this.toDateObject(date);
    if (!dateObj) return '';

    const localeId = this.getCurrentLocaleId();
    const currentLang = this.getCurrentLanguage();
    const monthAbbr = dateObj.toLocaleDateString(localeId, { month: 'short' });
    const month = this.applyLanguageSpecificMonthAbbreviation(monthAbbr, currentLang);
    const day = dateObj.getDate().toString().padStart(2, '0');
    const year = dateObj.getFullYear();

    const dateStr = this.formatDateString(day, month, year, currentLang);
    const timeStr = this.formatTimeInternal(dateObj, '2-digit', false);

    return `${dateStr} ${timeStr}`;
  }

  private formatDateTimeWithOptions(
    date: Date | string | number,
    options: { day: 'numeric' | '2-digit'; hour: 'numeric' | '2-digit' }
  ): string {
    const dateObj = this.toDateObject(date);
    if (!dateObj) return '';

    const localeId = this.getCurrentLocaleId();
    const dateStr = dateObj.toLocaleDateString(localeId, {
      year: 'numeric',
      month: 'long',
      day: options.day,
    });

    const timeStr = this.formatTimeInternal(dateObj, options.hour, false);

    return `${dateStr} ${timeStr}`;
  }

  /**
   * Format date and time with custom separator and options
   * @param date - Date to format
   * @param separator - Separator between date and time (default: ', ')
   * @param dateOptions - Intl.DateTimeFormatOptions for date formatting
   * @param timeOptions - Intl.DateTimeFormatOptions for time formatting
   * @returns Formatted date-time string
   */
  formatCustomDateTime(
    date: Date | string | number,
    separator: string = ', ',
    dateOptions?: Intl.DateTimeFormatOptions,
    timeOptions?: Intl.DateTimeFormatOptions
  ): string {
    if (!date) return '';

    const localeId = this.getCurrentLocaleId();
    const dateObj = new Date(date);

    if (isNaN(dateObj.getTime())) {
      return '';
    }

    const defaultDateOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };

    const defaultTimeOptions: Intl.DateTimeFormatOptions = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    };

    const dateStr = dateObj.toLocaleDateString(
      localeId,
      dateOptions || defaultDateOptions
    );

    const timeStr = dateObj.toLocaleTimeString(
      localeId,
      timeOptions || defaultTimeOptions
    );

    return `${dateStr}${separator}${timeStr}`;
  }

  formatNumber(value: number, decimals?: number): string {
    const localeId = this.getCurrentLocaleId();

    if (decimals !== undefined) {
      return value.toLocaleString(localeId, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    }

    return value.toLocaleString(localeId);
  }

  formatScoreValue(score: string | null | undefined, decimals: number = 1): string {
    if (score === null || score === undefined) {
      return '_';
    }
    
    const numValue = parseFloat(score);
    if (isNaN(numValue)) {
      return score;
    }
    
    const rounded = Math.round(numValue * Math.pow(10, decimals)) / Math.pow(10, decimals);
    
    const isWholeNumber = rounded % 1 === 0;
    return this.formatNumber(rounded, isWholeNumber ? 0 : decimals);
  }

  formatPercentage(value: number, decimals: number = 1): string {
    const localeId = this.getCurrentLocaleId();
    return (value / 100).toLocaleString(localeId, {
      style: 'percent',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }

  getCurrentLocaleId(): string {
    const currentLang = this.translocoService.getActiveLang();
    return LOCALE_MAP[currentLang] || 'en-US';
  }

  getCurrentLanguage(): string {
    return this.translocoService.getActiveLang();
  }

  formatTime(
    date: Date | string | number | null | undefined,
    includeSeconds: boolean = false
  ): string {
    const dateObj = this.toDateObject(date);
    if (!dateObj) return '';

    return this.formatTimeInternal(dateObj, 'numeric', includeSeconds);
  }

  private formatTimeInternal(
    dateObj: Date,
    hourFormat: 'numeric' | '2-digit',
    includeSeconds: boolean = false
  ): string {
    const localeId = this.getCurrentLocaleId();
    const currentLang = this.getCurrentLanguage();
    const use12Hour = this.shouldUse12HourFormat(currentLang);

    const options: Intl.DateTimeFormatOptions = {
      hour: hourFormat,
      minute: '2-digit',
      ...(includeSeconds && { second: '2-digit' }),
      hour12: use12Hour,
    };

    const formattedTime = dateObj.toLocaleTimeString(localeId, options);

    return this.applyCountrySpecificTimeFormatting(formattedTime, currentLang);
  }

  formatTimeWithSeconds(
    date: Date | string | number | null | undefined
  ): string {
    return this.formatTime(date, true);
  }

  private formatDateTimeWithSeparator(
    dateObj: Date,
    separator: string
  ): string {
    const localeId = this.getCurrentLocaleId();
    const currentLang = this.getCurrentLanguage();

    let dateStr: string;

    const day = dateObj.getUTCDate();
    const month = this.formatMonthAbbreviation(dateObj, localeId, currentLang);
    const year = dateObj.getUTCFullYear();

    if (currentLang === 'en') {
      dateStr = `${month} ${day}, ${year}`;
    } else if (currentLang === 'de') {
      dateStr = `${day}. ${month} ${year}`;
    } else {
      dateStr = `${day} ${month} ${year}`;
    }

    const timeStr = this.formatTime(dateObj);

    return `${dateStr}${separator}${timeStr}`;
  }

  private isValidDate(
    date: Date | string | number | null | undefined
  ): boolean {
    if (!date || (typeof date === 'string' && date.trim() === '')) {
      return false;
    }
    const dateObj =
      typeof date === 'string' || typeof date === 'number'
        ? new Date(date)
        : date;
    return !isNaN(dateObj.getTime());
  }

  private toDateObject(
    date: Date | string | number | null | undefined
  ): Date | null {
    if (!this.isValidDate(date)) {
      return null;
    }
    return typeof date === 'string' || typeof date === 'number'
      ? new Date(date)
      : (date as Date);
  }

  private shouldUse12HourFormat(currentLang: string): boolean {
    return currentLang === 'en' || currentLang === 'en-US';
  }

  private applyCountrySpecificTimeFormatting(
    timeString: string,
    currentLang: string
  ): string {
    switch (currentLang) {
      case 'fr-FR':
        return timeString.replace(':', ' h ');

      case 'fr-CA':
        return timeString.replace(' h ', ':');

      case 'de':
        return timeString;

      case 'pt-BR':
      case 'pt-PT':
        return timeString;

      case 'en':
      case 'en-US':
        return timeString;

      default:
        return timeString;
    }
  }

  private formatMonthAbbreviation(
    dateObj: Date,
    localeId: string,
    currentLang: string
  ): string {
    const utcDate = new Date(
      Date.UTC(
        dateObj.getUTCFullYear(),
        dateObj.getUTCMonth(),
        dateObj.getUTCDate(),
        12,
        0,
        0
      )
    );

    const monthAbbr = utcDate.toLocaleDateString(localeId, { month: 'short' });
    return this.applyLanguageSpecificMonthAbbreviation(monthAbbr, currentLang);
  }

  private applyLanguageSpecificMonthAbbreviation(
    monthAbbr: string,
    currentLang: string
  ): string {
    if (currentLang === 'it') {
      return ITALIAN_MONTH_ABBREVIATIONS[monthAbbr] || monthAbbr;
    }
    if (currentLang === 'de') {
      return GERMAN_MONTH_ABBREVIATIONS[monthAbbr] || monthAbbr;
    }
    if (currentLang === 'es-MX') {
      return SPANISH_MONTH_ABBREVIATIONS[monthAbbr] || monthAbbr;
    }
    return monthAbbr;
  }

  private formatDateString(
    day: string,
    month: string,
    year: number,
    currentLang: string
  ): string {
    if (currentLang === 'de') {
      return `${day}. ${month} ${year}`;
    }
    if (currentLang === 'en') {
      return `${month} ${day}, ${year}`;
    }
    return `${day} ${month} ${year}`;
  }

  formatDurationInSecondsWithLabel(
    seconds: number | null | undefined,
    includeZeroSeconds = false
  ): string {
    if (seconds == null || isNaN(seconds)) return '—';
  
    const totalSeconds = Math.floor(seconds);
    const minutes = Math.floor(totalSeconds / 60);
    const remainingSeconds = totalSeconds % 60;
  
    const minLabel = this.translocoService.translate(
      minutes === 1 ? 'MINUTE' : 'MINUTES',
      {},
      'reportingV2'
    );
    const secLabel = this.translocoService.translate(
      remainingSeconds === 1 ? 'SECOND' : 'SECONDS',
      {},
      'reportingV2'
    );
  
    if (minutes > 0 && remainingSeconds > 0) {
      return `${minutes} ${minLabel} ${remainingSeconds} ${secLabel}`;
    } else if (minutes > 0 && includeZeroSeconds) {
      const paddedSeconds = remainingSeconds.toString().padStart(2, '0');
      return `${minutes} ${minLabel} ${paddedSeconds} ${secLabel}`;
    } else if (minutes > 0) {
      return `${minutes} ${minLabel}`;
    } else {
      return `${remainingSeconds} ${secLabel}`;
    }
  }
}
